import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Archive, ArchiveRestore, KeyRound, Plus, UserRoundX } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { UserAvatar } from "@/components/common";
import { inputCls, pillGhost, pillSolid } from "@/components/canvas/LosBits";
import { useResource, invalidate } from "@/hooks/use-resource";

const ROLE = { Sales: "Salg", Engineering: "Ingeniør", "Sales manager": "Salgssjef", Management: "Ledelse" };

function PersonRow({ p, departments, canWrite, canAdmin, onArchive }) {
  const dept = departments.find((d) => d.code === p.department);
  return (
    <div className={cn("group flex items-center gap-3 px-4 py-2.5 text-[13px]", p.archived_at && "opacity-55")} data-testid={`team-person-${p.id}`} data-kind={p.kind}>
      <UserAvatar name={p.name} initials={p.initials} color={p.color} src={p.avatar_url} size="md" className="h-8 w-8" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 font-medium">{p.name}{p.archived_at ? <span className="text-[11px] font-normal text-muted-foreground">{p.kind === "user" ? "deaktivert" : "arkivert"}</span> : null}</div>
        <div className="truncate text-[12px] text-muted-foreground">{[p.title, dept ? `${dept.code} ${dept.name}` : null, p.note].filter(Boolean).join(" · ") || (p.kind === "user" ? "Bruker" : "Teammedlem")}</div>
      </div>
      {p.kind === "user" ? <span className="inline-flex items-center gap-1 rounded-md bg-[hsl(var(--v-ok-bg))] px-1.5 py-0.5 text-[11px] font-semibold text-[hsl(var(--v-ok))]" title="Har innlogging"><KeyRound className="h-3 w-3" /> {ROLE[p.role] || p.role || "Innlogging"}</span>
        : <span className="inline-flex items-center gap-1 rounded-md bg-black/[0.05] px-1.5 py-0.5 text-[11px] font-semibold text-foreground/60" title="Kan tildeles og nevnes – kan ikke logge inn"><UserRoundX className="h-3 w-3" /> uten innlogging</span>}
      {p.kind === "member" && canAdmin && !p.archived_at ? <Link to={`/settings/brukere?ny=${p.id}`} className="inline-flex h-7 items-center gap-1 rounded-full px-2 text-[12px] font-medium text-muted-foreground transition-opacity hover:bg-black/[0.05] hover:text-foreground md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100" data-testid={`team-grant-login-${p.id}`}><KeyRound className="h-3 w-3" /> Gi innlogging</Link> : null}
      {p.kind === "user" && canAdmin ? <Link to={`/settings/brukere?bruker=${p.id}`} className="inline-flex h-7 items-center rounded-full px-2 text-[12px] font-medium text-muted-foreground transition-opacity hover:bg-black/[0.05] hover:text-foreground md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100" data-testid={`team-open-user-${p.id}`}>Åpne</Link> : null}
      {p.kind === "member" && canWrite ? <button type="button" onClick={() => onArchive(p)} className="rounded-full p-1.5 text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" aria-label={p.archived_at ? "Gjenopprett" : "Arkiver"} data-testid={`team-archive-${p.id}`}>{p.archived_at ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}</button> : null}
    </div>
  );
}

function AddMember({ departments, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", title: "", department: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2 || saving) return;
    setSaving(true);
    try {
      await api.post("/team/members", { name: f.name.trim(), title: f.title.trim() || null, department: f.department || null });
      toast.success(`${f.name.trim()} er lagt til i teamlisten – uten innlogging`);
      setF({ name: "", title: "", department: "" });
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="inline-flex h-8 items-center gap-1 rounded-full border border-black/[0.1] bg-white px-3 text-[12.5px] font-medium hover:border-black/[0.25]" data-testid="team-add-member-open"><Plus className="h-3.5 w-3.5" /> Teammedlem uten innlogging</button>;
  return (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-2" data-testid="team-add-member-form">
      <input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Navn" className={cn(inputCls, "min-w-[160px]")} data-testid="team-add-member-name" />
      <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Rolle/tittel (valgfritt)" className={cn(inputCls, "min-w-[160px]")} data-testid="team-add-member-title" />
      <select value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} className={inputCls} aria-label="Avdeling" data-testid="team-add-member-dept">
        <option value="">Ingen avdeling</option>
        {departments.map((d) => <option key={d.id} value={d.code}>{d.code} {d.name}</option>)}
      </select>
      <button type="submit" disabled={f.name.trim().length < 2 || saving} className={pillSolid} data-testid="team-add-member-save">Legg til</button>
      <button type="button" onClick={() => setOpen(false)} className={pillGhost}>Avbryt</button>
    </form>
  );
}

/** Who can be assigned and mentioned: users with login, team members without, and LOS' departments. No access is granted here. */
export function TeamDirectory() {
  const { user } = useAuth();
  const canAdmin = user?.user_admin === true;
  const { data, loading } = useResource("/team", { archived: 1 });
  const people = data?.people || [];
  const departments = data?.departments || [];
  const canWrite = Boolean(data?.can_write);
  const refresh = () => invalidate("/team");
  const archive = async (p) => {
    try {
      await api.patch(`/team/members/${p.id}`, { archived: !p.archived_at });
      toast.success(p.archived_at ? "Gjenopprettet" : "Arkivert – kan ikke lenger tildeles");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3" data-testid="team-directory">
      <div className="rounded-[16px] border border-black/[0.06] bg-white">
        <div className="divide-y divide-black/[0.05]">
          {loading && !people.length ? <div className="px-4 py-3 text-[13px] text-muted-foreground">Laster teamet…</div> : people.map((p) => <PersonRow key={p.id} p={p} departments={departments} canWrite={canWrite} canAdmin={canAdmin} onArchive={archive} />)}
        </div>
        <div className="flex flex-wrap items-center gap-3 border-t border-black/[0.06] px-4 py-3">
          {canWrite ? <AddMember departments={departments} onDone={refresh} /> : null}
          <span className="text-[11.5px] text-muted-foreground">Medlemmer uten innlogging kan tildeles poster og nevnes i notater. Innlogging, roller og rettigheter styres under <Link to="/settings/brukere" className="font-medium text-foreground/80 underline-offset-2 hover:underline" data-testid="team-users-link">Brukere</Link>.</span>
        </div>
      </div>
      <div className="rounded-[16px] border border-black/[0.06] bg-white px-4 py-3" data-testid="team-departments">
        <div className="text-[12px] font-medium text-muted-foreground">Avdelinger (fra LOS' kalkylemal)</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {departments.map((d) => <span key={d.id} className="inline-flex h-7 items-center gap-1.5 rounded-full border border-black/[0.08] bg-white pl-1 pr-2.5 text-[12.5px]" title={d.note || undefined} data-testid={`team-dept-${d.code}`}><span className="inline-flex h-5 w-5 items-center justify-center rounded-[5px] font-mono text-[9.5px] font-semibold text-white" style={{ backgroundColor: d.color }}>{d.code}</span>{d.name}{d.note ? <span className="text-[11px] text-muted-foreground">?</span> : null}</span>)}
        </div>
      </div>
    </div>
  );
}
